import type {ChangeEvent} from 'react';

import {useFormField} from 'UI/components/action/inputs/hooks/useFormField';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * Custom hook for managing switch input state and validation within a form context. This hook provides
 * a comprehensive solution for handling switch components by integrating form field management, validation,
 * and change event handling. It combines the functionality of the base form field hook with switch-specific
 * logic to ensure proper state management and user interaction handling.
 *
 * @param name         The unique identifier for the switch field within the form.
 * @param initialValue The initial boolean state of the switch (true for checked, false for unchecked).
 * @param validation   Optional validation function to validate the switch state.
 * @param onChange     Callback function executed when the switch state changes.
 * @returns Object containing fieldRef for DOM reference, handleChange for event handling, and current value.
 *
 * @example
 * ```tsx
 * const {fieldRef, handleChange, value} = useSwitch(
 *     'notifications',
 *     false,
 *     (value) => value ? null : 'Please enable notifications',
 *     (e) => console.log('Switch changed:', e.target.checked)
 * );
 * ```
 */
export const useSwitch = (
    name: string,
    initialValue: boolean,
    validation: (ValidationFunc | undefined),
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
) => {
    const {fieldRef, handleFieldChange, value}
        = useFormField<boolean, HTMLInputElement>(name, initialValue, initialValue, false, validation);

    /**
     * Combines custom and built-in logic to handle changes to a checkbox input. This function is designed to facilitate
     * additional actions specified in the `onChange` callback while also integrating with the form's state management system
     * to update the checkbox's checked state. This ensures that all associated logic and state updates are handled consistently
     * and efficiently whenever the checkbox's state changes.
     *
     * @param e The change event object from the checkbox input, containing the new checked state.
     *
     * @example
     * ```ts
     * <input type="checkbox" onChange={handleChange} />
     * ```
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e);
        handleFieldChange(e.target.checked);
    };

    return {
        fieldRef,
        handleChange,
        value
    };
};